import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import Keycloak from 'keycloak-js';
import { catchError, finalize, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SharedModule } from '@shared/shared.module';
import { ApiService } from '@core/services/api';
import { AppTranslateLanguageService } from '@core/services/app-translate-language.service';
import { DirectionService } from '@core/services/direction.service';
import { UserEnvService } from '@core/services/user-env/user-env.service';

interface Company {
  id: string;
  description: string;
  [key: string]: any;
}

interface WorkingPeriod {
  id: string;
  description: string;
  active_period?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-select-working-period',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './select-working-period.component.html',
  styleUrls: ['./select-working-period.component.scss'],
})
export class SelectWorkingPeriodComponent implements OnInit {
  private readonly keycloak = inject(Keycloak);
  private readonly translate = inject(TranslateService);
  private readonly langService = inject(AppTranslateLanguageService);
  private readonly directionService = inject(DirectionService);
  private readonly fb = inject(FormBuilder);

  readonly isRtl = this.directionService.isRtl;
  private readonly api = inject(ApiService);
  private readonly userEnvService = inject(UserEnvService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly form = this.fb.group({
    company: this.fb.control<Company | null>(null, Validators.required),
    workingPeriod: this.fb.control<WorkingPeriod | null>(null, Validators.required),
  });

  readonly companyList = signal<Company[]>([]);
  readonly workingPeriodList = signal<WorkingPeriod[]>([]);
  readonly token = signal('');
  readonly alertShow = signal(false);
  readonly alertMessage = signal('');
  readonly changeStatus = signal(false);

  readonly loadingText = signal('');
  readonly showLoading = signal(false);

  ngOnInit(): void {
    if (!this.keycloak.authenticated) {
      this.router.navigateByUrl('/auth/select-keycloak-realm');
      return;
    }

    this.token.set(this.keycloak.token ?? '');
    const storedLanguage = this.langService.getStoredLanguage();
    if (storedLanguage !== this.translate.getCurrentLang()) {
      this.langService.useLanguage(storedLanguage);
    }
    this.setForm();
    this.getCompanies();
  }

  /** Clear both selects; pass values after company / period lists are loaded. */
  private setForm(state?: { company?: Company | null; workingPeriod?: WorkingPeriod | null }): void {
    if (!state) {
      this.form.reset({ company: null, workingPeriod: null }, { emitEvent: false });
      return;
    }
    const patch: { company?: Company | null; workingPeriod?: WorkingPeriod | null } = {};
    if ('company' in state) patch.company = state.company;
    if ('workingPeriod' in state) patch.workingPeriod = state.workingPeriod;
    this.form.patchValue(patch, { emitEvent: false });
  }

  getCompanies(): void {
    this.showLoadingFunc('loading');
    this.api
      .getFilteredData<Company[]>('company', { filter: { status: 'ACTIVE' } })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          this.handleError('error_data', error);
          return of(null);
        })
      )
      .subscribe({
        next: (companyListResponse) => {
          if (companyListResponse === undefined || !companyListResponse?.data) {
            this.handleError('error_data', 'error_data');
          } else {
            const list = companyListResponse.data as Company[];
            this.companyList.set(list);
            if (list.length > 0) {
              this.form.get('company')!.setValue(list[0]);
              this.onCompanyChange();
            } else {
              this.hideLoadingFunc();
            }
          }
        },
        error: (companyRequestError) => this.handleError('error', companyRequestError),
      });
  }

  onCompanyChange(): void {
    this.changeStatus.set(true);
    this.workingPeriodList.set([]);
    this.form.get('workingPeriod')!.reset();

    const selectedCompany: Company | null = this.form.get('company')!.value;
    if (!selectedCompany) return;

    const filter = {
      filter: {
        company: { id: selectedCompany.id },
        status: 'ACTIVE',
      },
    };
    this.api
      .getFilteredData<WorkingPeriod[]>('workingPeriod', filter)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.hideLoadingFunc();
          this.changeStatus.set(false);
        }),
        catchError((error) => {
          this.handleError('error_data', error);
          return of(null);
        })
      )
      .subscribe({
        next: (workingPeriodsResponse: { data?: WorkingPeriod[] } | null) => {
          if (workingPeriodsResponse?.data === undefined) {
            this.handleError('error_data', 'error_data');
          } else {
            const periods = workingPeriodsResponse.data as WorkingPeriod[];
            this.workingPeriodList.set(periods);
            const activePeriod = periods.find((d) => d.active_period === 'YES') ?? periods[0] ?? null;
            const selectedCompany = this.form.get('company')!.value as Company | null;
            this.setForm({ company: selectedCompany, workingPeriod: activePeriod });
          }
        },
        error: (workingPeriodRequestError) => this.handleError('error', workingPeriodRequestError),
      });
  }

  saveAndContinue(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertShow.set(true);
      return;
    }

    const { company, workingPeriod } = this.form.getRawValue();
    if (!company || !workingPeriod) {
      return;
    }

    const lang = this.translate.getCurrentLang() || this.langService.getStoredLanguage();
    this.langService.useLanguage(lang);
    const userEnvData = {
      working_period: {
        ...workingPeriod,
        data_source: workingPeriod['data_source'] ?? '',
      },
      company: {
        id: company.id,
        description: company.description,
        einvoice_user_type: company['einvoice_user_type'] ?? 'NO',
        edispatch_user_type: company['edispatch_user_type'] ?? 'NO',
        earchive_user_type: company['earchive_user_type'] ?? 'NO',
      },
      language: lang,
    };
    this.showLoadingFunc('loading');
    this.api
      .addData('user-env', userEnvData)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.hideLoadingFunc())
      )
      .subscribe({
        next: () => {
          this.userEnvService.applyFromRemote(userEnvData);
          this.router.navigateByUrl('/dashboard');
        },
        error: (error) => this.handleError('error', error),
      });
  }

  handleError(title: string, error: any): any {
    this.hideLoadingFunc();
    const msg =
      error?.error?.message ??
      error?.error?.error_description ??
      error?.message ??
      (typeof error === 'string' ? error : title);
    this.alertMessage.set(msg || 'An error occurred');
    this.alertShow.set(true);
    this.changeStatus.set(false);
    return of(null);
  }

  showLoadingFunc(text: string): void {
    this.loadingText.set(text);
    this.showLoading.set(true);
  }

  hideLoadingFunc(): void {
    this.loadingText.set('');
    this.showLoading.set(false);
  }

  back(): void {
    localStorage.removeItem('realm');
    this.userEnvService.clear();
    void this.keycloak.logout({ redirectUri: window.location.origin + '/auth' });
  }
}
